import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SURVEY_URL } from '@insite-store360/shared';
import { catchError, map, Observable } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';

export interface BrandVisibilityLookup {
  id: number;
  name: string;
  countryId?: number;
}

export interface BrandVisibilityFilters {
  countries: BrandVisibilityLookup[];
  retailers: BrandVisibilityLookup[];
  categories: BrandVisibilityLookup[];
}

export interface SnapshotScope {
  retailerCount: number;
  marketCount: number;
  month: number;
  year: number;
  lastMonth: number;
  lastYear: number;
}

export interface BrandVisibilityKpiCards {
  displayShare: BrandVisibilityShare;
  firstPageDisplayShare: BrandVisibilityShare;
  scope: SnapshotScope;
}

export interface MarketScorecardRow {
  countryId: number;
  countryName: string;
  displayShare: number;
  firstPageDisplayShare: number;
}

export interface MarketScorecard {
  month: number;
  year: number;
  markets: MarketScorecardRow[];
}

export interface DisplayShareTrendPoint {
  month: number;
  year: number;
  displayShare: number;
  brandCount: number;
  overallCount: number;
}

export interface DisplayShareComparisonPoint {
  month: number;
  year: number;
  displayShare: number;
  firstPageDisplayShare: number;
}

export interface DisplayShareComparison {
  year: number;
  points: DisplayShareComparisonPoint[];
}

export interface CategoryMixRow {
  categoryId: number;
  categoryName: string;
  modelCount: number;
  share: number;
}

export interface CategoryMix {
  month: number;
  year: number;
  totalCount: number;
  categories: CategoryMixRow[];
}

export interface PriceTrendPoint {
  month: number;
  year: number;
  electroluxAvg: number | null;
  competitorAvg: number | null;
}

export interface PriceTrend {
  year: number;
  points: PriceTrendPoint[];
}

export interface IncreaseInPriceDay {
  day: string;
  price: number | null;
}

export interface IncreaseInPriceModel {
  brandName: string;
  model: string;
  prices: IncreaseInPriceDay[];
}

export interface IncreaseInPriceByModels {
  countryId: number;
  retailerId: number;
  from: string;
  to: string;
  models: IncreaseInPriceModel[];
}

export interface DisplayShareTrend {
  year: number;
  points: DisplayShareTrendPoint[];
}

export interface BrandVisibilityFilter {
  countryIds?: number[];
  retailerIds?: number[];
  categoryIds?: number[];
}

export interface RetailerBrandShare {
  brandId: number;
  brandName: string;
  isPrimary: boolean;
  displayShare: number;
  modelCount: number;
  overallCount: number;
  rank: number;
}

export interface RetailerDisplayShare {
  month: number;
  year: number;
  brands: Array<{ id: number; name: string; isPrimary: boolean }>;
  retailers: Array<{
    retailerId: number;
    retailerName: string;
    shares: RetailerBrandShare[];
  }>;
}

export interface BrandVisibilityShare {
  displayShare: number;
  lastMonthShare: number;
  deltaPp: number;
  trend: 'up' | 'down' | 'flat';
  brandCount: number;
  overallCount: number;
  month: number;
  year: number;
  lastMonth: number;
  lastYear: number;
}

@Injectable({
  providedIn: 'root',
})
export class BrandVisibilityService {
  private readonly BASE = `${SURVEY_URL}/brand-visibility`;

  constructor(
    private readonly http: HttpClient,
    private readonly errorHandler: ErrorHandlerService,
  ) { }

  getFilters(): Observable<BrandVisibilityFilters> {
    return this.http.get<unknown>(`${this.BASE}/filters`).pipe(
      map((response) => {
        const wrapped = response as { result?: Partial<BrandVisibilityFilters> } & Partial<BrandVisibilityFilters>;
        const body = wrapped?.result?.countries ? wrapped.result : wrapped;
        return {
          countries: this.toLookups(body?.countries),
          retailers: this.toLookups(body?.retailers),
          categories: this.toLookups(body?.categories),
        };
      }),
      catchError(this.errorHandler.handleError),
    );
  }

  getRetailerDisplayShare(filter?: BrandVisibilityFilter): Observable<RetailerDisplayShare> {
    return this.http.get<unknown>(`${this.BASE}/retailer-display-share`, { params: this.toParams(filter) }).pipe(
      map((response) => this.toRetailerShare(response)),
      catchError(this.errorHandler.handleError),
    );
  }

  getRetailerFirstPageDisplayShare(filter?: BrandVisibilityFilter): Observable<RetailerDisplayShare> {
    return this.http
      .get<unknown>(`${this.BASE}/retailer-first-page-display-share`, { params: this.toParams(filter) })
      .pipe(
        map((response) => this.toRetailerShare(response)),
        catchError(this.errorHandler.handleError),
      );
  }

  getMarketScorecard(filter?: BrandVisibilityFilter): Observable<MarketScorecard> {
    return this.http.get<unknown>(`${this.BASE}/market-scorecard`, { params: this.toParams(filter) }).pipe(
      map((response) => {
        const wrapped = response as { result?: Partial<MarketScorecard> } & Partial<MarketScorecard>;
        const body = wrapped?.result?.markets ? wrapped.result : wrapped;
        return {
          month: Number(body?.month ?? 0),
          year: Number(body?.year ?? 0),
          markets: (body?.markets ?? []).map((row) => ({
            countryId: Number(row.countryId),
            countryName: row.countryName ?? '',
            displayShare: Number(row.displayShare ?? 0),
            firstPageDisplayShare: Number(row.firstPageDisplayShare ?? 0),
          })),
        };
      }),
      catchError(this.errorHandler.handleError),
    );
  }

  getDisplayShareTrend(filter?: BrandVisibilityFilter): Observable<DisplayShareTrend> {
    return this.http.get<unknown>(`${this.BASE}/display-share-trend`, { params: this.toParams(filter) }).pipe(
      map((response) => {
        const wrapped = response as { result?: Partial<DisplayShareTrend> } & Partial<DisplayShareTrend>;
        const body = wrapped?.result?.points ? wrapped.result : wrapped;
        return {
          year: Number(body?.year ?? 0),
          points: (body?.points ?? []).map((point) => ({
            month: Number(point.month ?? 0),
            year: Number(point.year ?? 0),
            displayShare: Number(point.displayShare ?? 0),
            brandCount: Number(point.brandCount ?? 0),
            overallCount: Number(point.overallCount ?? 0),
          })),
        };
      }),
      catchError(this.errorHandler.handleError),
    );
  }

  getDisplayShareComparison(filter?: BrandVisibilityFilter): Observable<DisplayShareComparison> {
    return this.http.get<unknown>(`${this.BASE}/display-share-comparison`, { params: this.toParams(filter) }).pipe(
      map((response) => {
        const wrapped = response as { result?: Partial<DisplayShareComparison> } & Partial<DisplayShareComparison>;
        const body = wrapped?.result?.points ? wrapped.result : wrapped;
        return {
          year: Number(body?.year ?? 0),
          points: (body?.points ?? []).map((point) => ({
            month: Number(point.month ?? 0),
            year: Number(point.year ?? 0),
            displayShare: Number(point.displayShare ?? 0),
            firstPageDisplayShare: Number(point.firstPageDisplayShare ?? 0),
          })),
        };
      }),
      catchError(this.errorHandler.handleError),
    );
  }

  getCategoryMix(filter?: BrandVisibilityFilter): Observable<CategoryMix> {
    return this.http.get<unknown>(`${this.BASE}/category-mix`, { params: this.toParams(filter) }).pipe(
      map((response) => {
        const wrapped = response as { result?: Partial<CategoryMix> } & Partial<CategoryMix>;
        const body = wrapped?.result?.categories ? wrapped.result : wrapped;
        return {
          month: Number(body?.month ?? 0),
          year: Number(body?.year ?? 0),
          totalCount: Number(body?.totalCount ?? 0),
          categories: (body?.categories ?? []).map((row) => ({
            categoryId: Number(row.categoryId),
            categoryName: row.categoryName ?? '',
            modelCount: Number(row.modelCount ?? 0),
            share: Number(row.share ?? 0),
          })),
        };
      }),
      catchError(this.errorHandler.handleError),
    );
  }

  getPriceTrend(filter?: BrandVisibilityFilter): Observable<PriceTrend> {
    return this.http.get<unknown>(`${this.BASE}/price-trend`, { params: this.toParams(filter) }).pipe(
      map((response) => {
        const wrapped = response as { result?: Partial<PriceTrend> } & Partial<PriceTrend>;
        const body = wrapped?.result?.points ? wrapped.result : wrapped;
        return {
          year: Number(body?.year ?? 0),
          points: (body?.points ?? []).map((point) => ({
            month: Number(point.month ?? 0),
            year: Number(point.year ?? 0),
            electroluxAvg: point.electroluxAvg == null ? null : Number(point.electroluxAvg),
            competitorAvg: point.competitorAvg == null ? null : Number(point.competitorAvg),
          })),
        };
      }),
      catchError(this.errorHandler.handleError),
    );
  }

  getIncreaseInPriceByModels(filter: {
    countryId: number;
    retailerId: number;
    from?: string;
    to?: string;
    brandId?: number;
    model?: string;
  }): Observable<IncreaseInPriceByModels> {
    const params: Record<string, string> = {
      countryId: String(filter.countryId),
      retailerId: String(filter.retailerId),
    };
    if (filter.from) {
      params['from'] = filter.from;
    }
    if (filter.to) {
      params['to'] = filter.to;
    }
    if (filter.brandId != null) {
      params['brandId'] = String(filter.brandId);
    }
    if (filter.model) {
      params['model'] = filter.model;
    }
    return this.http.get<unknown>(`${this.BASE}/increase-in-price-by-models`, { params }).pipe(
      map((response) => {
        const wrapped = response as { result?: Partial<IncreaseInPriceByModels> } & Partial<IncreaseInPriceByModels>;
        const body = wrapped?.result?.models ? wrapped.result : wrapped;
        return {
          countryId: Number(body?.countryId ?? 0),
          retailerId: Number(body?.retailerId ?? 0),
          from: body?.from ?? '',
          to: body?.to ?? '',
          models: (body?.models ?? []).map((item) => ({
            brandName: item.brandName ?? '',
            model: item.model ?? '',
            prices: (item.prices ?? []).map((point) => ({
              day: point.day ?? '',
              price: point.price == null ? null : Number(point.price),
            })),
          })),
        };
      }),
      catchError(this.errorHandler.handleError),
    );
  }

  getKpiCards(filter?: BrandVisibilityFilter): Observable<BrandVisibilityKpiCards> {
    return this.http.get<unknown>(`${this.BASE}/kpi-cards`, { params: this.toParams(filter) }).pipe(
      map((response) => {
        const wrapped = response as { result?: Partial<BrandVisibilityKpiCards> } & Partial<BrandVisibilityKpiCards>;
        const body = wrapped?.result?.displayShare ? wrapped.result : wrapped;
        const scope = body?.scope;
        return {
          displayShare: this.toShare(body?.displayShare),
          firstPageDisplayShare: this.toShare(body?.firstPageDisplayShare),
          scope: {
            retailerCount: Number(scope?.retailerCount ?? 0),
            marketCount: Number(scope?.marketCount ?? 0),
            month: Number(scope?.month ?? 0),
            year: Number(scope?.year ?? 0),
            lastMonth: Number(scope?.lastMonth ?? 0),
            lastYear: Number(scope?.lastYear ?? 0),
          },
        };
      }),
      catchError(this.errorHandler.handleError),
    );
  }

  exportProductVisibility(): Observable<Blob> {
    return this.http
      .get(`${this.BASE}/product-visibility/export`, { responseType: 'blob' })
      .pipe(catchError(this.errorHandler.handleError));
  }

  private toParams(filter?: BrandVisibilityFilter): Record<string, string> {
    const params: Record<string, string> = {};
    if (filter?.countryIds?.length) {
      params['countryId'] = filter.countryIds.join(',');
    }
    if (filter?.retailerIds?.length) {
      params['retailerId'] = filter.retailerIds.join(',');
    }
    if (filter?.categoryIds?.length) {
      params['categoryId'] = filter.categoryIds.join(',');
    }
    return params;
  }

  private toRetailerShare(response: unknown): RetailerDisplayShare {
    const wrapped = response as { result?: Partial<RetailerDisplayShare> } & Partial<RetailerDisplayShare>;
    const body = wrapped?.result?.retailers ? wrapped.result : wrapped;
    return {
      month: Number(body?.month ?? 0),
      year: Number(body?.year ?? 0),
      brands: (body?.brands ?? []).map((brand) => ({
        id: Number(brand.id),
        name: brand.name,
        isPrimary: brand.isPrimary === true,
      })),
      retailers: (body?.retailers ?? []).map((retailer) => ({
        retailerId: Number(retailer.retailerId),
        retailerName: retailer.retailerName,
        shares: (retailer.shares ?? []).map((share) => ({
          brandId: Number(share.brandId),
          brandName: share.brandName,
          isPrimary: share.isPrimary === true,
          displayShare: Number(share.displayShare ?? 0),
          modelCount: Number(share.modelCount ?? 0),
          overallCount: Number(share.overallCount ?? 0),
          rank: Number(share.rank ?? 0),
        })),
      })),
    };
  }

  private toLookups(rows?: BrandVisibilityLookup[]): BrandVisibilityLookup[] {
    return (rows ?? []).map((row) => ({
      id: Number(row.id),
      name: row.name,
      countryId: row.countryId == null ? undefined : Number(row.countryId),
    }));
  }

  private toShare(body?: Partial<BrandVisibilityShare>): BrandVisibilityShare {
    const rawTrend = body?.trend;
    return {
      displayShare: Number(body?.displayShare ?? 0),
      lastMonthShare: Number(body?.lastMonthShare ?? 0),
      deltaPp: Number(body?.deltaPp ?? 0),
      trend: rawTrend === 'up' || rawTrend === 'down' || rawTrend === 'flat' ? rawTrend : 'flat',
      brandCount: Number(body?.brandCount ?? 0),
      overallCount: Number(body?.overallCount ?? 0),
      month: Number(body?.month ?? 0),
      year: Number(body?.year ?? 0),
      lastMonth: Number(body?.lastMonth ?? 0),
      lastYear: Number(body?.lastYear ?? 0),
    };
  }
}
